<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <!-- Per Page -->
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ path: '/settings' }"
            >
              <i class="fa-solid fa-arrow-left"></i>
              <span class="ml-25">Back</span>
            </b-button>

            <h3 class="ml-1">Trainers</h3>
          </b-col>

          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <div class="d-flex align-items-center mr-1">
                <label>Show</label>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label>entries </label>
              </div>

              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button variant="primary" @click="addTrainer()">
                <span class="text-nowrap">Add Trainer</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refTrainerListTable"
          :outlined="true"
          :fields="fields"
          :items="filteredTrainers"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="editTrainer($event.id)"
        >
          <template #cell(name)="data">
            <div style="font-size: 1rem; max-width: 300px;" class="p-25">
              <p class="mb-0 font-weight-600">{{ data.value }}</p>
            </div>
          </template>

          <template #cell(email)="data">
            <div style="font-size: 0.9rem;" class="p-25">
              <small class="text-muted">{{ data.value }}</small>
            </div>
          </template>

          <template #cell(phone)="data">
            <div style="font-size: 0.9rem;" class="p-25">
              <small class="text-muted">{{ data.value || "N/A" }}</small>
            </div>
          </template>

          <template #cell(cv)="{ item }">
            <div class="p-25">
              <b-button
                v-if="item.bio"
                size="sm"
                variant="outline-info"
                @click="downloadCV(item.id)"
              >
                <i class="fa-solid fa-download mr-25"></i>
                Download CV
              </b-button>
              <small v-else class="text-muted">No CV</small>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-center">
              <b-button
                variant="warning"
                class="mr-1 p-50"
                @click.stop="editTrainer(item.id)"
              >
                <i class="fa-solid fa-pen-to-square mr-25"></i>
                <span> Edit</span>
              </b-button>
              <b-button
                variant="danger"
                class="p-50"
                @click.stop="deleteTrainer(item.id)"
              >
                <span>Delete</span>
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Trainer' : 'Add Trainer'"
      @ok="handleSubmit"
      @cancel="resetForm"
      ok-title="Save"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="trainer-name">
        <b-form-input
          id="trainer-name"
          v-model="form.name"
          placeholder="Enter trainer name"
          required
        />
      </b-form-group>
      <b-form-group label="Email *" label-for="trainer-email">
        <b-form-input
          id="trainer-email"
          v-model="form.email"
          type="email"
          placeholder="Enter trainer email"
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          required
        />
      </b-form-group>
      <b-form-group label="Phone" label-for="trainer-phone">
        <b-form-input
          id="trainer-phone"
          v-model="form.phone"
          placeholder="Enter trainer phone"
        />
      </b-form-group>
      <b-form-group label="CV (PDF)" label-for="trainer-cv">
        <b-form-file
          id="trainer-cv"
          v-model="cvFile"
          accept=".pdf"
          placeholder="Choose a PDF file or drop it here..."
          drop-placeholder="Drop PDF file here..."
        />
        <small class="text-muted" v-if="form.bio"
          >Current CV: {{ form.bio }}</small
        >
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BOverlay,
  BPagination,
  BModal,
  BFormGroup,
  BFormFile,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BOverlay,
    BPagination,
    BModal,
    BFormGroup,
    BFormFile,
    vSelect,
    ToastificationContent,
  },
  data() {
    return {
      loading: false,
      showModal: false,
      isEdit: false,
      trainers: [],
      form: {
        id: null,
        name: "",
        email: "",
        phone: "",
        bio: "", // CV filename
      },
      cvFile: null,
      fields: [
        {
          key: "name",
          label: "Trainer Name",
          sortable: true,
          thStyle: "color:white!important",
        },
        {
          key: "email",
          label: "Email",
          sortable: true,
          thStyle: "color:white!important",
        },
        {
          key: "phone",
          label: "Phone",
          sortable: true,
          thStyle: "color:white!important",
        },
        {
          key: "cv",
          label: "CV",
          sortable: false,
          class: "text-center",
          thStyle: "color:white!important",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          class: "text-center",
          thStyle: "color:white!important",
        },
      ],
      totalRows: 0,
      currentPage: 1,
      perPage: Number(localStorage.getItem("trainersShow") || 50),
      pageOptions: [10, 25, 50, 100],
      sortBy: "",
      sortDesc: false,
      filter: null,
      filterOn: ["name", "email", "phone"],
      perPageOptions: [10, 25, 50, 100],
    };
  },
  setup() {
    const MODULE = "settings";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, settingsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
  },
  computed: {
    dataMeta() {
      return {
        from:
          this.totalRows !== 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) <
          this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
    filteredTrainers() {
      if (!this.filter) return this.trainers;
      const term = this.filter.toLowerCase();
      return this.trainers.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(term)) ||
          (item.email && item.email.toLowerCase().includes(term))
      );
    },
  },
  watch: {
    perPage(val) {
      localStorage.setItem("trainersShow", val);
    },
  },
  created() {
    this.loadTrainers();
  },
  methods: {
    async loadTrainers() {
      try {
        this.loading = true;
        const response = await store.dispatch("settings/fetchTrainers");
        this.trainers = response.data.data || response.data;
        this.totalRows = this.trainers.length;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error loading trainers",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    addTrainer() {
      this.isEdit = false;
      this.resetForm();
      this.showModal = true;
    },
    editTrainer(id) {
      this.isEdit = true;
      const trainer = this.trainers.find((t) => t.id === id);
      if (trainer) {
        this.form = { ...trainer };
      }
      this.showModal = true;
    },
    async handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault();

      if (!this.form.name.trim() || !this.form.email.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill all required fields",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      if (!emailRegex.test(this.form.email.trim())) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please enter a valid email address",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        this.loading = true;

        // Create FormData for file upload
        const formData = new FormData();
        formData.append("name", this.form.name);
        formData.append("email", this.form.email);
        if (this.form.phone) {
          formData.append("phone", this.form.phone);
        }
        if (this.cvFile) {
          formData.append("cv_pdf", this.cvFile);
        }

        if (this.isEdit) {
          await store.dispatch("settings/updateTrainerWithFile", {
            id: this.form.id,
            formData,
          });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Trainer updated successfully",
              icon: "CheckIcon",
              variant: "success",
            },
          });
        } else {
          await store.dispatch("settings/createTrainerWithFile", formData);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Trainer created successfully",
              icon: "CheckIcon",
              variant: "success",
            },
          });
        }

        this.showModal = false;
        this.resetForm();
        this.loadTrainers();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error saving trainer",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteTrainer(id) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this trainer?", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then(async (value) => {
          if (value) {
            try {
              this.loading = true;
              await store.dispatch("settings/deleteTrainer", id);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Trainer deleted successfully",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              this.loadTrainers();
            } catch (error) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error deleting trainer",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            } finally {
              this.loading = false;
            }
          }
        });
    },
    resetForm() {
      this.form = {
        id: null,
        name: "",
        email: "",
        phone: "",
        bio: "",
      };
      this.cvFile = null;
    },
    async downloadCV(trainerId) {
      try {
        const response = await store.dispatch(
          "settings/downloadTrainerCV",
          trainerId
        );
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `trainer_cv_${trainerId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error downloading CV",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
